import { ErrorBody } from '../wire';

/**
 * What went wrong — and, on this surface, the question that replaces the staff
 * app's "is retrying sane?": **is this the server refusing, or the design
 * refusing?**
 *
 * The console has no outbox and nothing to queue. An operator action is a
 * request and its result is the truth, so there is exactly one failure type and
 * every screen renders it in the server's own words. What the console *does*
 * have to distinguish is the one code that is not a fault at all:
 *
 * `unsupported` (501) is `admin-api` turning Postgres' `42501` — a missing
 * grant on the `admin_api` role — into a sentence (`http.ts`, migration 0008).
 * It means a screen reached for something spec §6.3 says this surface must not
 * have: a tenant's book. That is the design speaking, and rendering it as a red
 * error toast would tell an operator to file a bug against a working lock. So
 * `isByDesign` is a predicate the views branch on, and the panel it paints says
 * what the rule is rather than what broke.
 */

export type FailureCode = ErrorBody['error']['code'] | 'offline' | 'malformed';

export class ApiFailure extends Error {
  readonly code: FailureCode;
  readonly status: number;
  readonly detail: Record<string, unknown> | undefined;

  constructor(
    code: FailureCode,
    message: string,
    options: { status?: number; detail?: Record<string, unknown> } = {},
  ) {
    super(message);
    this.name = 'ApiFailure';
    this.code = code;
    this.status = options.status ?? 0;
    this.detail = options.detail;
  }

  static offline(cause: string): ApiFailure {
    return new ApiFailure('offline', 'Could not reach admin-api.', { detail: { cause } });
  }

  /**
   * The server's `hint`, which is where `admin-api` puts the sentence that says
   * what to do next — "Enable them in the same request: clients, services,
   * calendar", "Revoke one first — issue new, switch, revoke old". Rendering the
   * message without it would throw away the half of the refusal that is useful.
   */
  get hint(): string | null {
    const hint = this.detail?.['hint'];
    return typeof hint === 'string' ? hint : null;
  }

  /** The list a refusal names — `modules`, `scopes`, `statuses`, `issues`. */
  get list(): string[] {
    for (const key of ['modules', 'scopes', 'statuses', 'issues', 'jobs']) {
      const value = this.detail?.[key];
      if (Array.isArray(value)) return value.map((v) => String(v));
    }
    return [];
  }
}

/**
 * `501 unsupported` on this surface is never a fault. See the note above.
 */
export function isByDesign(failure: ApiFailure): boolean {
  return failure.code === 'unsupported';
}

/** Turn a non-2xx response body into the failure it describes. */
export function failureFromBody(status: number, body: unknown): ApiFailure {
  const parsed = ErrorBody.safeParse(body);
  if (parsed.success) {
    return new ApiFailure(parsed.data.error.code, parsed.data.error.message, {
      status,
      ...(parsed.data.error.detail === undefined ? {} : { detail: parsed.data.error.detail }),
    });
  }
  // "Never a 200 with an error inside" is `admin-api`'s promise, not the load
  // balancer's. An HTML error page from Cloud Armor or IAP in front of it still
  // has to become one of our codes.
  return new ApiFailure(status >= 500 ? 'unavailable' : 'invalid', `HTTP ${status}.`, { status });
}

/** Anything thrown at a view, said in one line. */
export function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function asFailure(e: unknown): ApiFailure {
  return e instanceof ApiFailure ? e : new ApiFailure('unavailable', messageOf(e));
}
